<template>
  <!-- Teleport the modal to <body> so its z-60 stacking context is
       free of any parent transform/overflow traps. Matches V2FounderCta's
       pattern and how the donation widget escapes the page chrome to
       achieve a true full-screen takeover on mobile. -->
  <Teleport to="body">
    <transition name="v2-pem">
      <div
        v-if="open"
        class="v2-pem"
        @click.self="onBackdropClick"
      >
      <div
        ref="panelRef"
        class="v2-pem__panel"
        role="dialog"
        aria-modal="true"
        :aria-labelledby="stepHeadingId"
      >
        <!-- Floating close — sits above both columns at top-right. -->
        <button
          ref="closeBtnRef"
          type="button"
          class="v2-pem__close"
          aria-label="Close"
          @click="close"
        >
          <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2.25" aria-hidden="true">
            <path stroke-linecap="round" stroke-linejoin="round" d="M6 6l12 12M18 6L6 18"/>
          </svg>
        </button>

        <div class="v2-pem__split">
          <!-- ─── HERO COLUMN (left on desktop, top banner on mobile) ─── -->
          <aside class="v2-pem__hero">
            <span class="v2-pem__badge">{{ modalCopy.badge }}</span>
            <h2 class="v2-pem__hero-title">{{ modalCopy.headline }}</h2>
            <div
              v-if="modalCopy.body"
              class="v2-pem__hero-body"
              v-html="modalCopy.body"
            ></div>

            <ul class="v2-pem__pillars">
              <li
                v-for="(pillar, i) in modalCopy.benefits"
                :key="i"
                class="v2-pem__pillar"
              >
                <span class="v2-pem__pillar-icon" aria-hidden="true">
                  <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" v-html="pillarIconSvg(i)" />
                </span>
                <span class="v2-pem__pillar-text">
                  <strong>{{ pillar.title }}</strong>
                  <small v-if="pillar.body">{{ pillar.body }}</small>
                </span>
              </li>
            </ul>
          </aside>

          <!-- ─── FORM COLUMN ─── -->
          <section class="v2-pem__form-col">
            <!-- Heading — eyebrow mirrors the hero's PATIENT RESEARCH
                 PROGRAM badge so both columns open with the same
                 typographic vocabulary (pink uppercase → bold dark
                 title → secondary body). The active step number sits
                 in full brand pink against a muted-pink "of 03" so
                 progress reads at a glance without a separate
                 stepper element. Whole head crossfades on step
                 change via v2-pem-heading transition. -->
            <transition name="v2-pem-heading" mode="out-in">
              <div :key="currentStep" class="v2-pem__form-head">
                <span class="v2-pem__form-eyebrow" aria-hidden="true">
                  Step <strong>{{ stepNumberPadded }}</strong> of 03
                </span>
                <h3 :id="stepHeadingId" class="v2-pem__form-title">{{ currentHeading }}</h3>
                <p v-if="currentSubhead" class="v2-pem__form-sub">{{ currentSubhead }}</p>
              </div>
            </transition>

            <div class="v2-pem__sr-only" aria-live="polite" aria-atomic="true">
              Step {{ currentStep }} of 3
            </div>

            <!-- Steps stage -->
            <div class="v2-pem__steps-stage">
              <!-- STEP 1 — About You -->
              <transition :name="stepTransitionName">
                <div v-if="currentStep === 1" class="v2-pem__step-pane">
                  <div class="v2-pem__step">
                    <!-- Role dropdown (was chiclet selector — switched
                         to <select> per client feedback so the field
                         reads as a single decisive choice). -->
                    <div class="v2-pem__field">
                      <label for="v2pem-role">I am a… <span class="v2-pem__req" aria-hidden="true">*</span></label>
                      <select
                        id="v2pem-role"
                        v-model="form.role"
                        :aria-invalid="!!errors.role"
                      >
                        <option value="" disabled>Select one…</option>
                        <option
                          v-for="opt in roleOptions"
                          :key="opt.value"
                          :value="opt.value"
                        >{{ opt.label }}</option>
                      </select>
                      <p v-if="errors.role" class="v2-pem__err">{{ errors.role }}</p>
                    </div>

                    <div class="v2-pem__row">
                      <div class="v2-pem__field">
                        <label for="v2pem-first-name">First Name <span class="v2-pem__req" aria-hidden="true">*</span></label>
                        <input
                          id="v2pem-first-name"
                          v-model.trim="form.first_name"
                          type="text"
                          autocomplete="given-name"
                          maxlength="120"
                          :aria-invalid="!!errors.first_name"
                        />
                        <p v-if="errors.first_name" class="v2-pem__err">{{ errors.first_name }}</p>
                      </div>
                      <div class="v2-pem__field">
                        <label for="v2pem-last-name">Last Name <span class="v2-pem__req" aria-hidden="true">*</span></label>
                        <input
                          id="v2pem-last-name"
                          v-model.trim="form.last_name"
                          type="text"
                          autocomplete="family-name"
                          maxlength="120"
                          :aria-invalid="!!errors.last_name"
                        />
                        <p v-if="errors.last_name" class="v2-pem__err">{{ errors.last_name }}</p>
                      </div>
                    </div>

                    <div class="v2-pem__row">
                      <div class="v2-pem__field">
                        <label for="v2pem-email">Email <span class="v2-pem__req" aria-hidden="true">*</span></label>
                        <input
                          id="v2pem-email"
                          v-model.trim="form.email"
                          type="email"
                          autocomplete="email"
                          maxlength="191"
                          :aria-invalid="!!errors.email"
                        />
                        <p v-if="errors.email" class="v2-pem__err">{{ errors.email }}</p>
                      </div>
                      <div class="v2-pem__field">
                        <label for="v2pem-mobile-phone">Mobile Phone</label>
                        <input
                          id="v2pem-mobile-phone"
                          v-model.trim="form.mobile_phone"
                          type="tel"
                          autocomplete="tel"
                          maxlength="32"
                          :aria-invalid="!!errors.mobile_phone"
                        />
                        <p v-if="errors.mobile_phone" class="v2-pem__err">{{ errors.mobile_phone }}</p>
                      </div>
                    </div>

                    <div class="v2-pem__row">
                      <div class="v2-pem__field v2-pem__field--grow">
                        <label for="v2pem-city">City</label>
                        <input
                          id="v2pem-city"
                          v-model.trim="form.city"
                          type="text"
                          autocomplete="address-level2"
                          maxlength="120"
                        />
                      </div>
                      <div class="v2-pem__field v2-pem__field--state">
                        <label for="v2pem-state">State</label>
                        <select
                          id="v2pem-state"
                          v-model="form.state"
                        >
                          <option value="">--</option>
                          <option
                            v-for="s in usStates"
                            :key="s.code"
                            :value="s.code"
                          >{{ s.name }}</option>
                        </select>
                      </div>
                    </div>
                  </div>
                </div>
              </transition>

              <!-- STEP 2 — Your Story -->
              <transition :name="stepTransitionName">
                <div v-if="currentStep === 2" class="v2-pem__step-pane">
                  <div class="v2-pem__step">
                    <!-- Gender / Race share a 2-col row; Age sits alone
                         in a constrained-width row below so the longer
                         race labels (e.g. "Native Hawaiian or Pacific
                         Islander") don't crowd against age's 18-24 pill. -->
                    <div class="v2-pem__row">
                      <div class="v2-pem__field">
                        <label for="v2pem-gender">Gender <span class="v2-pem__req" aria-hidden="true">*</span></label>
                        <select
                          id="v2pem-gender"
                          v-model="form.gender"
                          :aria-invalid="!!errors.gender"
                        >
                          <option value="">Select…</option>
                          <option v-for="opt in genderOptions" :key="opt.value" :value="opt.value">{{ opt.label }}</option>
                        </select>
                        <p v-if="errors.gender" class="v2-pem__err">{{ errors.gender }}</p>
                      </div>
                      <div class="v2-pem__field">
                        <label for="v2pem-race">Race <span class="v2-pem__req" aria-hidden="true">*</span></label>
                        <select
                          id="v2pem-race"
                          v-model="form.race"
                          :aria-invalid="!!errors.race"
                        >
                          <option value="">Select…</option>
                          <option v-for="opt in raceOptions" :key="opt.value" :value="opt.value">{{ opt.label }}</option>
                        </select>
                        <p v-if="errors.race" class="v2-pem__err">{{ errors.race }}</p>
                      </div>
                    </div>

                    <div class="v2-pem__field v2-pem__field--half">
                      <label for="v2pem-age">Age <span class="v2-pem__req" aria-hidden="true">*</span></label>
                      <select
                        id="v2pem-age"
                        v-model="form.age_band"
                        :aria-invalid="!!errors.age_band"
                      >
                        <option value="">Select…</option>
                        <option v-for="opt in ageOptions" :key="opt.value" :value="opt.value">{{ opt.label }}</option>
                      </select>
                      <p v-if="errors.age_band" class="v2-pem__err">{{ errors.age_band }}</p>
                    </div>

                    <div class="v2-pem__field">
                      <label for="v2pem-diagnosis">A Little About Your Diagnosis &amp; Experience</label>
                      <textarea
                        id="v2pem-diagnosis"
                        v-model.trim="form.diagnosis_notes"
                        rows="5"
                        maxlength="5000"
                        :aria-invalid="!!errors.diagnosis_notes"
                      ></textarea>
                      <small class="v2-pem__hint">Optional — share whatever feels relevant. Anything you write stays confidential.</small>
                      <p v-if="errors.diagnosis_notes" class="v2-pem__err">{{ errors.diagnosis_notes }}</p>
                    </div>

                    <p v-if="topLevelError" class="v2-pem__banner v2-pem__banner--err">{{ topLevelError }}</p>
                  </div>
                </div>
              </transition>

              <!-- STEP 3 — Success -->
              <transition :name="stepTransitionName">
                <div v-if="currentStep === 3" class="v2-pem__step-pane">
                  <div class="v2-pem__success">
                    <div class="v2-pem__success-icon" aria-hidden="true">
                      <svg viewBox="0 0 24 24" width="34" height="34" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round">
                        <path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z"/>
                      </svg>
                    </div>
                    <span class="v2-pem__success-eyebrow">Request Received</span>
                    <h3 class="v2-pem__success-headline">Thank you for reaching out</h3>
                    <p class="v2-pem__success-body">
                      A confirmation is on its way to <strong>{{ form.email }}</strong>. Our research team will be in touch within a few business days to walk you through the next step in our precision medicine program.
                    </p>
                    <button
                      type="button"
                      class="v2-pem__btn v2-pem__btn--primary"
                      @click="close"
                    >
                      Close
                    </button>
                  </div>
                </div>
              </transition>
            </div>

            <!-- Action bar (hidden on Step 3 — close lives inline) -->
            <div v-if="currentStep < 3" class="v2-pem__actions">
              <!-- Uses the donation widget's `--lg` modifier so Back matches
                   the primary Submit Request button's height/font-size for
                   balanced action-row composition. Same convention as
                   ContactForm Step 4 (Donate side-by-side). -->
              <button
                v-if="currentStep > 1"
                type="button"
                class="dw-back-pill dw-back-pill--lg"
                @click="goToPreviousStep"
              >
                <svg viewBox="0 0 16 16" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true" class="dw-back-pill__icon">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M10 12L6 8l4-4"/>
                </svg>
                <span class="dw-back-pill__label">Back</span>
              </button>

              <button
                v-if="currentStep === 1"
                type="button"
                class="v2-pem__btn v2-pem__btn--continue"
                :disabled="submitting"
                @click="onStep1Next"
              >
                Next
                <svg viewBox="0 0 16 16" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2.25" aria-hidden="true">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M6 4l4 4-4 4"/>
                </svg>
              </button>

              <button
                v-if="currentStep === 2"
                type="button"
                class="v2-pem__btn v2-pem__btn--primary"
                :disabled="submitting"
                @click="onSubmit"
              >
                <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                  <path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z"/>
                </svg>
                {{ submitting ? 'Sending…' : 'Request Info' }}
              </button>
            </div>

            <p class="v2-pem__legal">
              By submitting, you agree to our privacy policy. Your information is kept strictly confidential.
            </p>
          </section>
        </div>
      </div>
    </div>
    </transition>
  </Teleport>
</template>

<script>
import { acquirePageScrollLock } from '../../../../composables/usePageScrollLock'
import StatesHash from '../../../../library/StatesHash.js'
import {
  ROLE_OPTIONS,
  GENDER_OPTIONS,
  RACE_OPTIONS,
  AGE_OPTIONS,
} from '../../../../library/EnrollmentOptions.js'

/**
 * V2PrecisionEnrollmentModal — split-screen takeover modal that walks a
 * visitor through the patient research program enrollment without
 * leaving the v2 parent campaign page. Pattern matches V2SupportModal:
 *  · Left hero pane: badge, headline, body, benefit pillars (admin-editable).
 *  · Right form pane: 3-step state machine with directional transitions.
 *  · Focus trap, ESC dismissal, sibling-inert isolation.
 *
 * Triggered by V2PrecisionAnalysis's "Request Enrollment" button via the
 * global mitt emitter event `open-precision-enrollment-modal`. The
 * parent campaign page subscribes and toggles a local ref that controls
 * the `open` prop.
 */

const STEP_HEADINGS = {
  1: 'Request Information',
  2: 'Tell Us Your Story',
  3: 'Request Received',
}

const STEP_SUBHEADS = {
  1: 'Fill out the form and our team will contact you.',
  2: 'A few details so we can route your case to the right team.',
  3: '',
}

const DEFAULT_BENEFITS = [
  { title: 'Personalized Roadmap', body: 'Therapies ranked by your tumor’s biology' },
  { title: '100% Confidential', body: 'HIPAA-compliant data protection' },
  { title: 'No Cost to Patients', body: 'Fully funded by donors' },
]

const PILLAR_ICONS = [
  // Map / clipboard (Personalized Roadmap)
  '<path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z"/>'
    + '<polyline points="14 2 14 8 20 8"/>'
    + '<line x1="9" y1="13" x2="15" y2="13"/>'
    + '<line x1="9" y1="17" x2="13" y2="17"/>',
  // Shield-check (Confidential)
  '<path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z"/>'
    + '<polyline points="9 12 12 15 18 9"/>',
  // Heart (No Cost)
  '<path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z"/>',
]

function emptyForm() {
  return {
    role: '',
    first_name: '',
    last_name: '',
    email: '',
    mobile_phone: '',
    city: '',
    state: '',
    gender: '',
    race: '',
    age_band: '',
    diagnosis_notes: '',
  }
}

export default {
  name: 'V2PrecisionEnrollmentModal',
  props: {
    open: { type: Boolean, required: true },
    /**
     * Admin-editable copy for the hero column. Keys (badge, headline,
     * body, benefits[]) map to precision_analysis.enrollment_cta in the
     * foundation template content. Empty fields fall back to the
     * Lovable-mockup defaults baked into this component.
     */
    modalContent: { type: Object, default: () => ({}) },
    /**
     * Campaign id submitted alongside the enrollment so the
     * notification email can name the page the request came from.
     */
    campaignId: { type: [String, Number, null], default: null },
  },
  emits: ['close', 'submitted'],
  data() {
    return {
      currentStep: 1,
      transitionDirection: 'forward',
      submitting: false,
      errors: {},
      topLevelError: null,
      form: emptyForm(),
      previouslyFocusedEl: null,
    }
  },
  computed: {
    stepHeadingId() { return 'v2-pem-step-heading' },
    currentHeading() { return STEP_HEADINGS[this.currentStep] },
    currentSubhead() { return STEP_SUBHEADS[this.currentStep] },
    stepNumberPadded() { return String(this.currentStep).padStart(2, '0') },
    stepTransitionName() {
      return this.transitionDirection === 'backward' ? 'v2-pem-step-back' : 'v2-pem-step-fwd'
    },
    roleOptions() { return ROLE_OPTIONS },
    genderOptions() { return GENDER_OPTIONS },
    raceOptions() { return RACE_OPTIONS },
    ageOptions() { return AGE_OPTIONS },
    usStates() {
      // Exclude COFA-associated sovereign nations (Micronesia, Marshall Islands,
      // Palau) — they appear in some USPS lists but aren't US states/territories.
      // Form submits the 2-letter code; dropdown renders the full name.
      const EXCLUDED = new Set(['FM', 'MH', 'PW'])
      return Object.entries(StatesHash)
        .filter(([code]) => !EXCLUDED.has(code))
        .map(([code, name]) => ({ code, name }))
        .sort((a, b) => a.name.localeCompare(b.name))
    },
    modalCopy() {
      const c = this.modalContent || {}
      const adminBenefits = Array.isArray(c.modal_benefits) ? c.modal_benefits : []
      const benefits = []
      for (let i = 0; i < 3; i++) {
        const admin = adminBenefits[i] || {}
        const fallback = DEFAULT_BENEFITS[i] || { title: '', body: '' }
        benefits.push({
          title: admin.title || fallback.title,
          body: admin.body || fallback.body,
        })
      }
      return {
        badge: c.modal_badge || 'PATIENT RESEARCH PROGRAM',
        headline: c.modal_headline || 'Join Our Precision Medicine Study',
        body: c.modal_body || '<p>Your tumor’s unique biology is analyzed to identify the most promising treatment options, at no cost to you.</p>',
        benefits,
      }
    },
  },
  watch: {
    open(nowOpen) {
      if (nowOpen) {
        this.resetState()
        this.$nextTick(() => {
          this.captureFocusAndTrap()
          this.inertSiblings()
        })
        if (!this._releasePageScroll) this._releasePageScroll = acquirePageScrollLock()
        document.addEventListener('keydown', this.onKeydown)
      } else {
        this._releasePageScroll?.()
        this._releasePageScroll = null
        document.removeEventListener('keydown', this.onKeydown)
        this.restoreInertSiblings()
        this.restoreFocus()
      }
    },
  },
  beforeUnmount() {
    this._releasePageScroll?.()
    this._releasePageScroll = null
    document.removeEventListener('keydown', this.onKeydown)
    this.restoreInertSiblings()
  },
  methods: {
    pillarIconSvg(i) {
      return PILLAR_ICONS[i % PILLAR_ICONS.length] || ''
    },
    close() {
      this.$emit('close')
    },
    onBackdropClick() {
      // Don't dismiss on backdrop while submitting — visitor may have
      // bumped the edge of the panel by accident.
      if (this.submitting) return
      this.close()
    },
    resetState() {
      this.currentStep = 1
      this.submitting = false
      this.errors = {}
      this.topLevelError = null
      this.form = emptyForm()
      this.transitionDirection = 'forward'
    },

    /* ─── Focus management ──────────────────────────────── */
    captureFocusAndTrap() {
      this.previouslyFocusedEl = document.activeElement
      if (this.$refs.closeBtnRef) this.$refs.closeBtnRef.focus({ preventScroll: true })
    },
    restoreFocus() {
      if (this.previouslyFocusedEl && this.previouslyFocusedEl.focus) {
        this.previouslyFocusedEl.focus({ preventScroll: true })
      }
    },
    enforceFocusTrap(e) {
      const panel = this.$refs.panelRef
      if (!panel) return
      const focusable = panel.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )
      if (!focusable.length) return
      const first = focusable[0]
      const last = focusable[focusable.length - 1]
      if (!panel.contains(document.activeElement)) {
        e.preventDefault()
        first.focus()
        return
      }
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault(); last.focus()
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault(); first.focus()
      }
    },
    inertSiblings() {
      // Walk up from the modal, marking each ancestor's *siblings*
      // inert. Keeps the rest of the page untabbable + hidden from
      // assistive tech while leaving the modal's ancestor chain live.
      //
      // We start from `panelRef` (a DOM ref inside the rendered
      // modal) instead of `this.$el` because the component is
      // teleported to <body>; Vue 3 reports `this.$el` as a
      // comment-node placeholder at the original instantiation
      // site, and walking up from that path would eventually mark
      // the teleported modal itself as inert (because it's a
      // sibling of the #app root in <body>).
      const start = this.$refs.panelRef
      if (!start) return
      this._inertedEls = []
      let node = start
      while (node && node !== document.body && node.parentElement) {
        for (const sib of node.parentElement.children) {
          if (sib === node) continue
          const tag = sib.tagName
          if (tag === 'SCRIPT' || tag === 'STYLE' || tag === 'LINK') continue
          this._inertedEls.push({ el: sib, ariaHidden: sib.getAttribute('aria-hidden'), inert: sib.inert })
          sib.setAttribute('aria-hidden', 'true')
          if ('inert' in sib) sib.inert = true
        }
        node = node.parentElement
      }
    },
    restoreInertSiblings() {
      if (!this._inertedEls) return
      this._inertedEls.forEach(({ el, ariaHidden, inert }) => {
        if (ariaHidden === null) el.removeAttribute('aria-hidden')
        else el.setAttribute('aria-hidden', ariaHidden)
        if ('inert' in el) el.inert = inert
      })
      this._inertedEls = null
    },
    onKeydown(e) {
      if (e.key === 'Escape') {
        this.close()
        return
      }
      if (e.key === 'Tab') {
        this.enforceFocusTrap(e)
      }
    },

    /* ─── Navigation ─────────────────────────────────────── */
    goToPreviousStep() {
      if (this.currentStep <= 1) return
      this.transitionDirection = 'backward'
      this.currentStep -= 1
    },
    onStep1Next() {
      if (!this.validateStep1()) return
      this.transitionDirection = 'forward'
      this.currentStep = 2
    },

    /* ─── Validation ─────────────────────────────────────── */
    validateStep1() {
      const e = {}
      if (!this.form.role) e.role = 'Please choose one.'
      if (!this.form.first_name) e.first_name = 'First name is required.'
      if (!this.form.last_name)  e.last_name  = 'Last name is required.'
      if (!this.form.email) {
        e.email = 'Email is required.'
      } else if (!/^\S+@\S+\.\S+$/.test(this.form.email)) {
        e.email = 'Enter a valid email address.'
      }
      this.errors = { ...this.errors, ...e }
      return Object.keys(e).length === 0
    },
    validateStep2() {
      const e = {}
      if (!this.form.gender)   e.gender   = 'Required.'
      if (!this.form.race)     e.race     = 'Required.'
      if (!this.form.age_band) e.age_band = 'Required.'
      this.errors = { ...this.errors, ...e }
      return Object.keys(e).length === 0
    },

    /* ─── Submit ─────────────────────────────────────────── */
    async onSubmit() {
      if (!this.validateStep2()) return
      this.topLevelError = null
      this.submitting = true
      try {
        await window.axios.post('/api/precision-enrollment', {
          ...this.form,
          campaign_id: this.campaignId || null,
        })
        this.transitionDirection = 'forward'
        this.currentStep = 3
        this.$emit('submitted', { email: this.form.email })
      } catch (err) {
        const response = err && err.response
        if (response && response.status === 422 && response.data && response.data.errors) {
          // Map server-side validation errors to the form.
          const serverErrors = {}
          for (const [key, list] of Object.entries(response.data.errors)) {
            serverErrors[key] = Array.isArray(list) ? list[0] : String(list)
          }
          this.errors = { ...this.errors, ...serverErrors }
          // If a Step 1 field failed server-side, bounce back so the
          // visitor can fix it.
          const step1Keys = ['role', 'first_name', 'last_name', 'email', 'mobile_phone', 'city', 'state']
          if (step1Keys.some(k => k in serverErrors)) {
            this.transitionDirection = 'backward'
            this.currentStep = 1
          } else {
            this.topLevelError = 'Please review the fields above and try again.'
          }
        } else {
          this.topLevelError = 'We couldn’t submit your request right now. Please try again in a moment.'
        }
      } finally {
        this.submitting = false
      }
    },
  },
}
</script>

<style scoped>
/* ─── Modal frame ───────────────────────────────────────── */
/* Backdrop matches the donation widget's `donation-modal-backdrop`:
   solid black @ 70% opacity, no blur. Keeps the lightbox vocabulary
   consistent across modals (donate, support, precision enrollment). */
.v2-pem {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.70);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 60;
  padding: 24px;
}
/* Width narrowed to ~960px to track the Lovable mockup's proportions
   (Lovable rendered closer to 1000px including viewport chrome). */
/* Flat panel — no elevation shadow, matching the donation modal's
   `.donation-modal-panel` (which sits flush against the backdrop
   with zero box-shadow). The dark backdrop already provides enough
   visual separation. */
.v2-pem__panel {
  position: relative;
  width: min(960px, 92vw);
  max-height: 92vh;
  background: #ffffff;
  border-radius: 22px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  font-family: 'Red Hat Display', sans-serif;
}

/* Close button matches the donation widget's pink-bubble pattern:
   solid brand pink (#DA1884) circle with a white X icon. The donate
   widget hangs it half-off the panel corner via `md:-top-2 md:-right-4`;
   our panel has `overflow: hidden` so we keep it just inside the
   corner (no clipping) but reuse the visual treatment so visitors
   recognize "close" the same way across modals. */
/* Flat-pink circle, no shadow — matches the donation widget's
   modal-close convention (md:bg-pink rounded-full, no elevation).
   Hover/active feedback comes from background-darken + transform
   only, same vocabulary as `.monthly-overlay__btn--primary` in
   DonationForm.vue. */
.v2-pem__close {
  position: absolute;
  top: 12px;
  right: 12px;
  z-index: 6;
  background: #DA1884;
  border: none;
  color: #ffffff;
  width: 32px;
  height: 32px;
  border-radius: 9999px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: background 180ms ease, transform 180ms cubic-bezier(0.22, 1, 0.36, 1);
}
.v2-pem__close:hover { background: #BC0E65; transform: scale(1.06); }
.v2-pem__close:active { background: #BC0E65; transform: scale(0.98); }
.v2-pem__close:focus-visible {
  outline: 3px solid rgba(218, 24, 132, 0.4);
  outline-offset: 2px;
}
@media (prefers-reduced-motion: reduce) {
  .v2-pem__close,
  .v2-pem__close:hover,
  .v2-pem__close:active { transform: none; }
}

/* ─── Two-pane split ────────────────────────────────────── */
.v2-pem__split {
  flex: 1;
  display: flex;
  min-height: 0;
}

/* ─── HERO COLUMN (left) ────────────────────────────────── */
/* 50/50 split with the form column — matches the Lovable mockup
   where the value pitch sits on roughly equal footing with the
   action. Padding tightened a touch to suit the narrower panel. */
.v2-pem__hero {
  flex: 0 0 50%;
  background: linear-gradient(165deg, #1D2549 0%, #131A38 100%);
  color: #ffffff;
  padding: 44px 38px 40px;
  display: flex;
  flex-direction: column;
  gap: 20px;
  min-height: 0;
  overflow: hidden;
}
.v2-pem__badge {
  display: inline-block;
  align-self: flex-start;
  color: #DA1884;
  font-size: 13px;
  font-weight: 800;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  line-height: 1;
}
.v2-pem__hero-title {
  margin: 0;
  font-size: 38px;
  line-height: 1.08;
  font-weight: 800;
  letter-spacing: -0.015em;
  color: #ffffff;
  text-wrap: balance;
}
.v2-pem__hero-body {
  font-size: 16.5px;
  line-height: 1.55;
  color: rgba(255, 255, 255, 0.82);
}
.v2-pem__hero-body :deep(p) { margin: 0; }
.v2-pem__hero-body :deep(p + p) { margin-top: 10px; }
.v2-pem__hero-body :deep(strong) { color: #ffffff; font-weight: 700; }

/* Pillars sit directly under the hero body — panel is now content-
   sized (no fixed 680px height), so the prior `margin-top:auto`
   bottom-anchor produced an empty mid-pane void. Stack tight under
   the body copy with a generous gap between each pillar. */
.v2-pem__pillars {
  list-style: none;
  margin: 6px 0 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 22px;
}
.v2-pem__pillar {
  display: flex;
  gap: 14px;
  align-items: flex-start;
}
.v2-pem__pillar-icon {
  flex: 0 0 auto;
  width: 40px;
  height: 40px;
  border-radius: 11px;
  background: rgba(218, 24, 132, 0.16);
  color: #DA1884;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.v2-pem__pillar-text {
  display: flex;
  flex-direction: column;
  gap: 3px;
  font-family: 'Red Hat Display', sans-serif;
}
.v2-pem__pillar-text strong {
  font-size: 16px;
  font-weight: 700;
  color: #ffffff;
  line-height: 1.2;
}
.v2-pem__pillar-text small {
  font-size: 14px;
  color: rgba(255, 255, 255, 0.72);
  line-height: 1.4;
}

/* ─── FORM COLUMN ───────────────────────────────────────── */
.v2-pem__form-col {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  background: #ffffff;
}

.v2-pem__form-head {
  padding: 20px 32px 12px;
}
/* Step-progress eyebrow — pairs with the hero's PATIENT RESEARCH
   PROGRAM badge for column symmetry. Muted-pink "Step ___ of 03"
   frame, active number in full brand pink so progress reads at a
   glance without a separate stepper. */
.v2-pem__form-eyebrow {
  display: block;
  margin: 0 0 10px;
  color: rgba(218, 24, 132, 0.55);
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  line-height: 1;
}
.v2-pem__form-eyebrow strong {
  color: #DA1884;
  font-weight: 800;
}
.v2-pem__form-title {
  margin: 0;
  font-size: 32px;
  font-weight: 800;
  line-height: 1.12;
  letter-spacing: -0.018em;
  color: #1D2549;
}
.v2-pem__form-sub {
  margin: 8px 0 0;
  color: #4B5563;
  font-size: 15.5px;
  line-height: 1.5;
}

.v2-pem__sr-only {
  position: absolute;
  width: 1px; height: 1px;
  padding: 0; margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

/* Steps stage — grid layout so multiple step-panes overlap in the
   same cell during transitions (panel is now auto-height; a flex
   stage with absolute panes would collapse to zero). The grid sizes
   to whichever step is taller, giving the panel a content-driven
   height per step. */
.v2-pem__steps-stage {
  position: relative;
  display: grid;
  grid-template-areas: 'stack';
  overflow: hidden;
}
.v2-pem__step-pane {
  grid-area: stack;
  overflow-y: auto;
  padding: 6px 32px 14px;
}
.v2-pem__step {
  display: flex;
  flex-direction: column;
  gap: 18px;
}

/* ─── Form primitives ───────────────────────────────────── */
.v2-pem__row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}
.v2-pem__field {
  display: flex;
  flex-direction: column;
  gap: 7px;
  min-width: 0;
  /* Anchor for absolutely-positioned `.v2-pem__err` so validation
     messages float in the gap below the input without growing the
     field. Keeps Step 1 inside the panel's height budget when
     multiple errors fire at once (no scroll). */
  position: relative;
}
.v2-pem__field--grow { /* used in mobile-stack contexts */ }
/* Half-width standalone field — used for Age on Step 2 so the
   dropdown reads as a single specific choice rather than crowding
   the longer race/gender selects above. */
.v2-pem__field--half {
  max-width: calc(50% - 8px);
}
.v2-pem__field label,
.v2-pem__legend {
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0;
  text-transform: none;
  color: #1D2549;
  line-height: 1.2;
  padding: 0;
}
.v2-pem__legend { margin-bottom: 2px; }
.v2-pem__req { color: #DA1884; margin-left: 2px; }

.v2-pem__field input,
.v2-pem__field select,
.v2-pem__field textarea {
  box-sizing: border-box;
  padding: 12px 14px;
  border: 1px solid #D1D5DB;
  border-radius: 10px;
  font-size: 15px;
  font-family: 'Red Hat Display', system-ui, sans-serif;
  color: #1D2549;
  background: #ffffff;
  width: 100%;
  /* Pre-allocate the focus halo with a transparent shadow so the
     transition to a colored halo on focus reads as a color change
     rather than a shape appearing. 3px halo matches the donation
     widget convention. */
  box-shadow: 0 0 0 3px transparent;
  transition: border-color 200ms cubic-bezier(0.22, 1, 0.36, 1),
              box-shadow 200ms cubic-bezier(0.22, 1, 0.36, 1),
              background 200ms ease;
}
.v2-pem__field select {
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  background-image: url("data:image/svg+xml;charset=utf-8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16' fill='none' stroke='%236B7280' stroke-width='2'><path stroke-linecap='round' stroke-linejoin='round' d='M4 6l4 4 4-4'/></svg>");
  background-repeat: no-repeat;
  background-position: right 12px center;
  background-size: 14px;
  padding-right: 34px;
}
.v2-pem__field textarea {
  resize: vertical;
  min-height: 110px;
  line-height: 1.55;
}
.v2-pem__field input:focus,
.v2-pem__field select:focus,
.v2-pem__field textarea:focus {
  outline: none;
  border-color: #F0A3CE;
  box-shadow: 0 0 0 3px rgba(218, 24, 132, 0.15);
}
.v2-pem__field input[aria-invalid="true"],
.v2-pem__field select[aria-invalid="true"],
.v2-pem__field textarea[aria-invalid="true"] {
  border-color: #EF4444;
  background-color: #FEF2F2;
  box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.12);
}
/* Chrome autofill — keep the field looking like the default state
   (white bg, dark navy text) instead of Chrome's yellow tint, which
   can cause a perceived "snap" on focus. */
.v2-pem__field input:-webkit-autofill,
.v2-pem__field input:-webkit-autofill:hover,
.v2-pem__field input:-webkit-autofill:focus {
  -webkit-text-fill-color: #1D2549;
  -webkit-box-shadow: 0 0 0 30px #ffffff inset, 0 0 0 3px transparent;
  caret-color: #1D2549;
}
.v2-pem__field input:-webkit-autofill:focus {
  -webkit-box-shadow: 0 0 0 30px #ffffff inset, 0 0 0 3px rgba(218, 24, 132, 0.15);
}
.v2-pem__hint {
  font-size: 14px; /* was 12px — confidentiality reassurance under the diagnosis box, bumped for mobile legibility (client 2026-05-29) */
  color: #6B7280;
  line-height: 1.4;
}
/* Validation message floats below its field without taking layout
   height. Sits in the 18px step-gap below the input. Single-line
   client-side errors ("First name is required.") fit comfortably;
   the rare wrapped server error visually bleeds into the next row's
   label area, which is an accepted trade-off for never-scrolling
   forms. */
.v2-pem__err {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  margin: 2px 0 0;
  color: #DC2626;
  font-size: 12.5px;
  line-height: 1.2;
  pointer-events: none;
}
.v2-pem__banner {
  margin: 0;
  padding: 10px 14px;
  border-radius: 9px;
  font-size: 13px;
}
.v2-pem__banner--err {
  background: #FEF2F2;
  color: #B91C1C;
  border: 1px solid #FECACA;
}

/* Role radio pills */
.v2-pem__radio-row {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}
.v2-pem__radio-pill {
  flex: 0 1 auto;
  padding: 10px 18px;
  border-radius: 9999px;
  border: 1.5px solid #D1D5DB;
  background: #ffffff;
  color: #374151;
  font-family: 'Red Hat Display', sans-serif;
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 0.01em;
  cursor: pointer;
  transition: background 180ms ease, color 180ms ease,
              border-color 180ms ease, transform 180ms cubic-bezier(0.22, 1, 0.36, 1);
}
.v2-pem__radio-pill:hover {
  border-color: #009CDE;
  color: #1D2549;
}
.v2-pem__radio-pill.is-active {
  background: #009CDE;
  border-color: #009CDE;
  color: #ffffff;
}
.v2-pem__radio-pill:focus-visible {
  outline: 3px solid rgba(0, 156, 222, 0.4);
  outline-offset: 2px;
}

/* ─── Action bar ────────────────────────────────────────── */
/* Buttons sit as a centered group: Back pill on the left, primary
   CTA on the right, both at natural width with an 8px gap between
   them. Step 1's lone Next button centers on its own via the
   parent's `justify-content: center` — no per-button margin auto
   needed. */
.v2-pem__actions {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 6px 32px 6px;
  background: #ffffff;
}
.v2-pem__actions > .dw-back-pill--lg {
  flex: 0 0 auto;
}
.v2-pem__actions > .v2-pem__btn--primary {
  flex: 0 0 auto;
}

.v2-pem__legal {
  margin: 6px auto 0;
  max-width: 420px;
  padding: 0 32px 14px;
  text-align: center;
  color: #9CA3AF;
  font-size: 11.5px;
  line-height: 1.5;
  letter-spacing: 0.005em;
}

/* Buttons match the donation widget convention: flat brand colors,
   no gradients. Brand blue (rest #009CDE / hover #0075A7) sourced
   from sass/_variables.scss via the donate-widget tribute palette;
   mirrors `.monthly-overlay__btn--primary` in DonationForm.vue.
   Both Next (Step 1) and the primary submit/close (Steps 2 & 3)
   use the same blue family so every action-bar CTA reads as one
   visual category. */
.v2-pem__btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px 22px;
  border-radius: 9999px;
  border: 2px solid transparent;
  cursor: pointer;
  font-family: 'Red Hat Display', sans-serif;
  font-size: 15px;
  font-weight: 700;
  letter-spacing: 0.04em;
  transition: background 180ms ease, color 180ms ease,
              transform 180ms cubic-bezier(0.22, 1, 0.36, 1),
              opacity 180ms ease;
}
/* Flat CTAs — match the donation widget's `.monthly-overlay__btn--
   primary` pattern: solid brand color, hover darkens one shade,
   transform translateY(-1px) provides "lift" without elevation
   shadows. No box-shadow anywhere; the same flat vocabulary as
   DonationForm.vue, MakeDonationWidget.vue, and the modal-close. */
.v2-pem__btn--continue {
  background: #009CDE;
  color: #ffffff;
  padding: 13px 28px;
  /* Typography matches `.v2-pem__btn--primary` (uppercase + 0.06em
     letter-spacing) so "NEXT" reads as the same button family as
     "REQUEST INFO" and "CLOSE" across the modal's three steps. */
  text-transform: uppercase;
  letter-spacing: 0.06em;
}
.v2-pem__btn--continue:hover {
  background: #0075A7;
  transform: translateY(-1px);
}
.v2-pem__btn--continue:active {
  background: #0075A7;
  transform: translateY(0) scale(0.98);
}
.v2-pem__btn--continue:focus-visible {
  outline: 2px solid #0075A7;
  outline-offset: 2px;
}
.v2-pem__btn--continue:disabled {
  opacity: 0.55; cursor: not-allowed; transform: none; background: #009CDE;
}

.v2-pem__btn--primary {
  background: #009CDE;             /* Brand blue — sass/_variables.scss */
  color: #ffffff;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  /* Padding + font-size + 1.5px border match `.contact__submit` and
     `.dw-back-pill--lg` exactly so the Back/Primary pair in the
     action row reads as one balanced pill — same height, same
     typography weight as the donation widget. */
  padding: 10px 20px;
  font-size: 1rem;
  border: 1.5px solid transparent;
}
.v2-pem__btn--primary:hover {
  background: #0075A7;
  transform: translateY(-1px);
}
.v2-pem__btn--primary:active {
  background: #0075A7;
  transform: translateY(0) scale(0.98);
}
.v2-pem__btn--primary:focus-visible {
  outline: 2px solid #0075A7;
  outline-offset: 2px;
}
.v2-pem__btn--primary:disabled {
  opacity: 0.55; cursor: not-allowed; transform: none; background: #009CDE;
}
@media (prefers-reduced-motion: reduce) {
  .v2-pem__btn,
  .v2-pem__btn--continue:hover,
  .v2-pem__btn--continue:active,
  .v2-pem__btn--primary:hover,
  .v2-pem__btn--primary:active { transform: none !important; }
}

/* ─── Step 3 success ────────────────────────────────────── */
.v2-pem__success {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  gap: 14px;
  height: 100%;
  padding: 24px 28px;
}
.v2-pem__success-icon {
  width: 64px;
  height: 64px;
  border-radius: 9999px;
  background: rgba(34, 197, 94, 0.15);
  color: #16A34A;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.v2-pem__success-eyebrow {
  color: #DA1884;
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.18em;
  text-transform: uppercase;
}
.v2-pem__success-headline {
  margin: 0;
  font-size: 24px;
  font-weight: 800;
  color: #1D2549;
}
.v2-pem__success-body {
  margin: 0;
  max-width: 36ch;
  color: #4B5563;
  font-size: 14.5px;
  line-height: 1.55;
}
.v2-pem__success-body strong {
  color: #1D2549;
  font-weight: 700;
}

/* ─── Open/close motion ─────────────────────────────────────
   Mirrors the donation widget's `.donation-modal-*` animation
   pair (MakeDonationWidget.vue) so the precision-enrollment
   takeover opens and closes with the exact same choreography:
   backdrop crossfades while the panel scale-and-translates in
   from below. Timing values, easings, and delays match the
   donation modal byte-for-byte. Driven via CSS animations
   rather than transitions so the delays (80ms panel-in, 40ms
   backdrop-out) line up with the donation widget. */
.v2-pem-enter-active { animation: v2-pem-backdrop-in 320ms cubic-bezier(0.4, 0, 0.2, 1) both; }
.v2-pem-leave-active { animation: v2-pem-backdrop-out 260ms cubic-bezier(0.4, 0, 1, 1) 40ms both; }
.v2-pem-enter-active .v2-pem__panel {
  animation: v2-pem-panel-in 460ms cubic-bezier(0.16, 1, 0.3, 1) 80ms both;
  transform-origin: center;
  will-change: transform, opacity;
}
.v2-pem-leave-active .v2-pem__panel {
  animation: v2-pem-panel-out 220ms cubic-bezier(0.4, 0, 1, 1) both;
}

@keyframes v2-pem-backdrop-in {
  from { opacity: 0; }
  to   { opacity: 1; }
}
@keyframes v2-pem-backdrop-out {
  from { opacity: 1; }
  to   { opacity: 0; }
}
@keyframes v2-pem-panel-in {
  from { opacity: 0; transform: translateY(28px) scale(0.96); }
  to   { opacity: 1; transform: translateY(0)    scale(1); }
}
@keyframes v2-pem-panel-out {
  from { opacity: 1; transform: translateY(0)   scale(1); }
  to   { opacity: 0; transform: translateY(12px) scale(0.985); }
}

/* ─── Step pane slide transitions ───────────────────────── */
.v2-pem-step-fwd-enter-active,
.v2-pem-step-fwd-leave-active,
.v2-pem-step-back-enter-active,
.v2-pem-step-back-leave-active {
  transition: transform 460ms cubic-bezier(0.22, 1, 0.36, 1),
              opacity 280ms ease;
  will-change: transform, opacity;
}
.v2-pem-step-fwd-enter-from  { transform: translateX(36px);  opacity: 0; }
.v2-pem-step-fwd-leave-to    { transform: translateX(-36px); opacity: 0; }
.v2-pem-step-back-enter-from { transform: translateX(-36px); opacity: 0; }
.v2-pem-step-back-leave-to   { transform: translateX(36px);  opacity: 0; }
.v2-pem-step-fwd-leave-active,
.v2-pem-step-back-leave-active { z-index: 2; }
.v2-pem-step-fwd-enter-active,
.v2-pem-step-back-enter-active { z-index: 1; }

/* Heading crossfade */
.v2-pem-heading-enter-active {
  transition: opacity 200ms ease, transform 240ms cubic-bezier(0.22, 1, 0.36, 1);
}
.v2-pem-heading-leave-active {
  transition: opacity 140ms ease, transform 200ms cubic-bezier(0.4, 0, 0.2, 1);
}
.v2-pem-heading-enter-from { opacity: 0; transform: translateY(-6px); }
.v2-pem-heading-leave-to   { opacity: 0; transform: translateY(6px); }

@media (prefers-reduced-motion: reduce) {
  .v2-pem-step-fwd-enter-active,
  .v2-pem-step-fwd-leave-active,
  .v2-pem-step-back-enter-active,
  .v2-pem-step-back-leave-active,
  .v2-pem-heading-enter-active,
  .v2-pem-heading-leave-active {
    transition-duration: 80ms !important;
    transform: none !important;
  }
  .v2-pem-step-fwd-enter-from,
  .v2-pem-step-fwd-leave-to,
  .v2-pem-step-back-enter-from,
  .v2-pem-step-back-leave-to,
  .v2-pem-heading-enter-from,
  .v2-pem-heading-leave-to {
    transform: none !important;
  }
  .v2-pem-enter-active,
  .v2-pem-leave-active,
  .v2-pem-enter-active .v2-pem__panel,
  .v2-pem-leave-active .v2-pem__panel {
    animation-duration: 80ms !important;
    animation-delay: 0ms !important;
  }
}

/* ─── Mobile (≤1023px): stack hero on top, form below ───── */
/* ─── Tablet & Mobile (≤1023px) ─────────────────────────────
   Full-screen takeover with hero stacked on top of the form.
   Generous default sizing — pulls back the desktop typographic
   scale (24px title, 14px labels, 15-16px inputs) so the form
   feels intentionally proportioned rather than crammed. The
   ≤480px override below tightens just enough to keep Step 1
   inside an iPhone SE viewport.

   The `min-height: 0` chain (panel → split → form-col → steps-
   stage) lets the action bar sit anchored to the bottom of the
   viewport while the stage absorbs the remaining height. */
@media (max-width: 1023px) {
  /* Mobile = total screen takeover. Backdrop goes transparent so
     the panel reads as a hard takeover (no dark frame visible
     during the scale-in animation), mirroring the donation
     widget's `lg:bg-black/[0.70]` (desktop-only backdrop) pattern. */
  .v2-pem {
    padding: 0;
    background: transparent;
  }
  .v2-pem__panel {
    width: 100vw;
    height: 100vh;
    /* Dynamic viewport — accounts for iOS Safari's hidden URL bar
       and the soft keyboard so the action bar doesn't get pushed
       below the visible area while a field is focused. */
    height: 100dvh;
    /* Override desktop's `max-height: 92vh` — on mobile we want a
       true edge-to-edge takeover with no margin around the panel,
       matching the donation widget's full-screen mobile behavior. */
    max-height: none;
    border-radius: 0;
    max-width: none;
  }
  .v2-pem__split {
    flex-direction: column;
    flex: 1;
    min-height: 0;
  }
  /* Hero on phones: badge + headline only. Pillars and body copy
     are hidden to free up vertical real-estate so Steps 1 and 2 fit
     within an iPhone-14-class viewport (390×844) without scrolling
     — Rick's explicit ask. The headline alone establishes the
     program identity; benefits return in admin-editable hero copy
     on desktop where there's room.
     Top/bottom padding kept equal so the eyebrow + title block sits
     visually centered — Rick asked for the dark header to read as a
     symmetric banner, not weighted toward the top. */
  .v2-pem__hero {
    flex: 0 0 auto;
    order: 1;
    padding: 44px 28px 44px;
    gap: 8px;
  }
  .v2-pem__hero-title {
    font-size: 30px;
    line-height: 1.12;
    letter-spacing: -0.018em;
  }
  .v2-pem__badge { font-size: 12.5px; letter-spacing: 0.2em; }
  .v2-pem__hero-body { display: none; }
  .v2-pem__pillars { display: none; }

  /* Form column absorbs the rest of the viewport. The chain of
     `flex: 1` + `min-height: 0` propagates through to the steps-
     stage so it shrinks correctly instead of pushing the action
     bar off-screen. */
  .v2-pem__form-col {
    order: 2;
    flex: 1 1 auto;
    min-height: 0;
  }
  /* Form-head spacing trimmed so the title + subhead read as a
     tight introduction rather than eating budget meant for fields. */
  .v2-pem__form-head { padding: 18px 28px 8px; }
  .v2-pem__form-eyebrow {
    font-size: 11.5px;
    letter-spacing: 0.22em;
    margin-bottom: 6px;
  }
  .v2-pem__form-title { font-size: 24px; line-height: 1.1; }
  .v2-pem__form-sub { display: block; font-size: 16px; margin-top: 4px; } /* was 14px — mobile shrank below desktop; bump for legibility (client 2026-05-29) */

  /* Steps-pane vertical rhythm: each row tightens to fit two field
     rows + the role select inside the available height. 16px input
     font kills iOS Safari's focus-zoom; 10px vertical padding keeps
     touch targets comfortable while being more compact than the
     prior 14px. */
  .v2-pem__step-pane { padding: 6px 28px 10px; }
  .v2-pem__step { gap: 12px; }
  .v2-pem__row { gap: 10px; grid-template-columns: 1fr 1fr; }
  .v2-pem__field { gap: 4px; }
  .v2-pem__field label,
  .v2-pem__legend { font-size: 13px; }
  .v2-pem__field input,
  .v2-pem__field select,
  .v2-pem__field textarea {
    padding: 10px 12px;
    font-size: 16px;
  }
  .v2-pem__field textarea { min-height: 100px; }
  .v2-pem__radio-row { gap: 10px; }
  .v2-pem__radio-pill { padding: 12px 20px; font-size: 14.5px; }

  /* City + State row: Rick's call — narrow City a touch, widen
     State so the dropdown isn't squeezed into a 90px column. The
     1.1fr/1fr split keeps City still slightly wider but State now
     has room for full state names if we ever switch from 2-letter
     codes. */
  .v2-pem__row:has(.v2-pem__field--state) {
    grid-template-columns: 1.1fr 1fr;
  }
  .v2-pem__field--state { max-width: none; }
  .v2-pem__field--half { max-width: 55%; }

  /* Action row: drop the heart icon next to Request Info (Rick
     okayed ditching icons; matches the donation widget's icon-less
     CONTINUE). The parent's `justify-content: center` handles
     centering for both the lone Next button (Step 1) and the
     Back+Primary pair (Step 2). */
  .v2-pem__actions { padding: 10px 28px 4px; }
  .v2-pem__actions > .v2-pem__btn--primary > svg { display: none; }

  .v2-pem__legal {
    padding: 0 28px 10px;
    margin-top: 4px;
    font-size: 11.5px;
    line-height: 1.4;
  }
}

/* ─── Small-phone tightening (≤480px) ────────────────────────
   On the smallest phones (iPhone SE ~375×667) we just trim the
   horizontal gutters so the form fields don't crowd against the
   viewport edges. Vertical rhythm and typography are already
   compact enough at the ≤1023px tier (Rick's "no-scroll-on-phone"
   requirement), so this tier just nudges horizontal padding. */
@media (max-width: 480px) {
  .v2-pem__hero { padding-left: 22px; padding-right: 22px; }
  .v2-pem__form-head { padding-left: 22px; padding-right: 22px; }
  .v2-pem__step-pane { padding-left: 22px; padding-right: 22px; }
  .v2-pem__actions { padding-left: 22px; padding-right: 22px; }
  .v2-pem__legal { padding-left: 22px; padding-right: 22px; }
}
/* On short mobile screens, scroll the stacked card rather than shrinking
   the field pane below the height of a single input. Close stays outside
   this scroll region, anchored to the panel. */
@media (max-width: 1023px) and (max-height: 500px) {
  .v2-pem__split {
    overflow-y: auto;
    overscroll-behavior-y: contain;
    -webkit-overflow-scrolling: touch;
  }
  .v2-pem__form-col {
    flex: 1 0 360px;
    min-height: 360px;
  }
}
</style>
